import React, { useEffect, useState, useMemo } from "react";
import { X, Camera, Play } from "lucide-react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { useI18n } from "../context/I18nContext";

const mediaSrc = (g) => g.media_url || g.image || "";
const isVideoItem = (g) => g.media_type === "video";

export default function Gallery() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [activeAlbum, setActiveAlbum] = useState("all");

  useEffect(() => { api.get("/gallery").then(r => setItems(r.data || [])).catch(() => {}); }, []);

  const albums = useMemo(() => {
    const set = new Set();
    items.forEach(i => { if (i.album) set.add(i.album); });
    return Array.from(set).sort();
  }, [items]);

  const visible = activeAlbum === "all"
    ? items
    : items.filter(i => (i.album || "") === activeAlbum);

  return (
    <Layout>
      <div data-testid="gallery-page">
        <section className="hero-gradient py-24">
          <div className="max-w-6xl mx-auto px-5 lg:px-10">
            <div className="text-xs uppercase tracking-[0.3em] text-warm-orange font-semibold">{t("gallery.tag")}</div>
            <h1 className="font-display text-deep-blue text-5xl sm:text-6xl lg:text-7xl leading-tight mt-3">{t("gallery.t1")}<br/><span className="italic text-warm-orange">{t("gallery.t2")}</span></h1>
          </div>
        </section>
        <section className="py-16 bg-ivory">
          <div className="max-w-6xl mx-auto px-5 lg:px-10">
            {albums.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-albums">
                <button
                  onClick={() => setActiveAlbum("all")}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition ${activeAlbum === "all" ? "bg-gold text-deep-blue border-gold" : "bg-white text-deep-blue border-black/10 hover:border-gold"}`}
                  data-testid="album-all"
                >
                  {t("gallery.all") || "Tout"} ({items.length})
                </button>
                {albums.map(a => {
                  const count = items.filter(i => i.album === a).length;
                  return (
                    <button
                      key={a}
                      onClick={() => setActiveAlbum(a)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition ${activeAlbum === a ? "bg-gold text-deep-blue border-gold" : "bg-white text-deep-blue border-black/10 hover:border-gold"}`}
                      data-testid={`album-${a.replace(/\s+/g, "-")}`}
                    >
                      {a} ({count})
                    </button>
                  );
                })}
              </div>
            )}
            {visible.length === 0 ? (
              <div className="p-12 rounded-2xl bg-pearl text-center">
                <Camera className="text-warm-orange mx-auto mb-3" size={40}/>
                <p className="font-display text-2xl text-deep-blue">{t("gallery.empty1")}</p>
                <p className="text-deep-blue/60 mt-2">{t("gallery.empty2")}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {visible.map(g => {
                  const src = mediaSrc(g);
                  const video = isVideoItem(g);
                  return (
                    <figure key={g.id} className="flex flex-col" data-testid={`gallery-${g.id}`}>
                      {g.caption && (
                        <figcaption className="mb-3 px-1 font-display text-2xl md:text-3xl font-bold text-warm-orange leading-tight tracking-tight">
                          {g.caption}
                        </figcaption>
                      )}
                      <button
                        onClick={() => setOpen(g)}
                        className="aspect-square rounded-xl overflow-hidden group relative bg-pearl"
                        aria-label={g.caption || (video ? "vidéo" : "photo")}
                        data-testid={video ? `gallery-video-${g.id}` : `gallery-image-${g.id}`}
                      >
                        {video ? (
                          <>
                            <video src={src} muted playsInline preload="metadata" className="w-full h-full object-cover"/>
                            <span className="absolute inset-0 grid place-items-center bg-deep-blue/25 group-hover:bg-deep-blue/40 transition">
                              <span className="w-14 h-14 rounded-full bg-warm-orange/95 grid place-items-center shadow-lg">
                                <Play className="text-ivory ml-1" size={26} fill="currentColor"/>
                              </span>
                            </span>
                          </>
                        ) : (
                          <img src={src} alt={g.caption || ""} className="w-full h-full object-cover group-hover:scale-105 transition duration-500"/>
                        )}
                        {g.album && (
                          <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-deep-blue/85 text-ivory backdrop-blur-sm uppercase tracking-widest">
                            {g.album}
                          </span>
                        )}
                      </button>
                    </figure>
                  );
                })}
              </div>
            )}
          </div>
        </section>
        {open && (
          <div className="fixed inset-0 z-[60] bg-deep-blue/95 grid place-items-center p-5" onClick={() => setOpen(null)} data-testid="lightbox">
            <button onClick={() => setOpen(null)} className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 text-ivory grid place-items-center hover:bg-gold hover:text-deep-blue" data-testid="lightbox-close"><X/></button>
            <div className="max-w-5xl w-full" onClick={e => e.stopPropagation()}>
              {open.caption && <div className="mb-4 font-display font-bold text-warm-orange text-3xl md:text-4xl text-center leading-tight">« {open.caption} »</div>}
              {isVideoItem(open) ? (
                <video
                  src={mediaSrc(open)}
                  controls
                  autoPlay
                  className="w-full max-h-[75vh] object-contain rounded-lg bg-black"
                  data-testid="lightbox-video"
                />
              ) : (
                <img src={mediaSrc(open)} alt={open.caption || ""} className="w-full max-h-[75vh] object-contain rounded-lg" data-testid="lightbox-image"/>
              )}
              {open.album && <div className="mt-2 text-center text-xs uppercase tracking-widest text-gold">{open.album}</div>}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
